import { useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MeterFlowChart } from '../components/MeterFlowChart';
import { MeterAlertConsumptionChart } from '../components/MeterAlertConsumptionChart';
import { MeterInspectorToolbar } from '../components/MeterInspectorToolbar';
import {
  MeterCustomersCard,
  MeterDetailCards,
  MeterIdentityCard,
} from '../components/MeterInfoPanel';
import { type AlertEvent, MeterAlertHistoryCard } from '../components/MeterAlertHistoryCard';
import { MeterAlertDetailModal } from '../components/MeterAlertDetailModal';
import { MeterPropertiesCard, MeterSensorGroupsCard } from '../components/MeterSensorGroupsPanel';
import { MeterLifetimeConsumptionChart } from '../components/MeterLifetimeConsumptionChart';
import { useMeterFlow } from '../hooks/useMeterFlow';
import { useMeterClassicFlow } from '../hooks/useMeterClassicFlow';
import { useMeterEvents } from '../hooks/useMeterEvents';
import { useMeterReads } from '../hooks/useMeterReads';
import { useMeterDetail } from '../hooks/useMeterDetail';
import { useMeterAlerts } from '../hooks/useMeterAlerts';
import { useMeterSensorGroups } from '../hooks/useMeterSensorGroups';

export function MeterDetailPage() {
  const { sensorId } = useParams<{ sensorId: string }>();
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: new Date(Date.now() - 7 * 86400000),
    to: new Date(),
  });
  const [timezoneOverride, setTimezoneOverride] = useState<string | null>(null);
  const { data: flowData, isLoading: flowLoading, error: flowError } = useMeterFlow(
    sensorId,
    dateRange,
  );
  const classicFlowQuery = useMeterClassicFlow(sensorId, dateRange);
  const { data: eventsData } = useMeterEvents(sensorId, dateRange);
  const { data: readsData } = useMeterReads(sensorId, dateRange);
  const { data: detailData, isLoading: detailLoading, error: detailError } = useMeterDetail(
    sensorId,
  );
  const { data: sgData, isLoading: sgLoading } = useMeterSensorGroups(sensorId);
  const { data: alertsData, isLoading: alertsLoading } = useMeterAlerts(sensorId);
  const [hoveredAlertIndex, setHoveredAlertIndex] = useState<number | null>(null);
  const [selectedAlert, setSelectedAlert] = useState<AlertEvent | null>(null);
  const allAlerts = useMemo(
    () => [...(alertsData?.activeAlerts ?? []), ...(alertsData?.endedAlerts ?? [])],
    [alertsData],
  );

  return (
    <div className='space-y-6'>
      <div className='flex items-center gap-4'>
        <Button variant='ghost' size='icon' asChild>
          <Link to='/meter-inspector'>
            <ArrowLeft className='h-4 w-4' />
          </Link>
        </Button>
        <div>
          <h1 className='text-2xl font-bold'>Meter Detail</h1>
          {
            /* Blank rather than an em-dash while the detail request is in flight, so the
              subtitle doesn't flash a placeholder on every load. */
          }
          <p className='text-muted-foreground'>
            {detailLoading ? '' : `Device ID: ${detailData?.detail?.deviceId ?? '—'}`}
          </p>
        </div>
      </div>

      <MeterCustomersCard
        customers={detailData?.customers ?? []}
        isLoading={detailLoading}
        sensorId={sensorId ? Number(sensorId) : null}
      />

      <MeterIdentityCard
        detail={detailData?.detail ?? null}
        isLoading={detailLoading}
        error={detailError}
      />

      <MeterInspectorToolbar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        timezoneOverride={timezoneOverride}
        onTimezoneChange={setTimezoneOverride}
        meterTimezone={detailData?.detail?.timezone ?? null}
      />

      <MeterAlertHistoryCard
        activeAlerts={alertsData?.activeAlerts ?? []}
        endedAlerts={alertsData?.endedAlerts ?? []}
        isLoading={alertsLoading}
        hoveredAlertIndex={hoveredAlertIndex}
        onAlertHover={setHoveredAlertIndex}
        onAlertClick={setSelectedAlert}
      />

      <MeterAlertDetailModal
        alert={selectedAlert}
        sensorId={sensorId!}
        open={!!selectedAlert}
        onOpenChange={(open) => !open && setSelectedAlert(null)}
        timezoneOverride={timezoneOverride}
      />

      <MeterAlertConsumptionChart
        flow={flowData?.flow}
        classicFlow={classicFlowQuery.data?.flow}
        classicIsLoading={classicFlowQuery.isLoading}
        classicIsFetching={classicFlowQuery.isFetching}
        classicError={classicFlowQuery.error}
        onClassicRetry={() => classicFlowQuery.refetch()}
        allAlerts={allAlerts}
        isLoading={flowLoading || alertsLoading}
        hoveredAlertIndex={hoveredAlertIndex}
        onAlertHover={setHoveredAlertIndex}
        dateRange={dateRange}
      />

      <MeterFlowChart
        flow={flowData?.flow}
        events={eventsData?.events}
        reads={readsData?.reads}
        isLoading={flowLoading}
        error={flowError}
        dateRange={dateRange}
        timezoneOverride={timezoneOverride}
      />

      <MeterSensorGroupsCard
        sensorGroups={sgData?.sensorGroups ?? []}
        isLoading={sgLoading}
      />

      <MeterPropertiesCard
        properties={sgData?.properties ?? []}
        isLoading={sgLoading}
      />

      <MeterDetailCards
        detail={detailData?.detail ?? null}
      />

      <MeterLifetimeConsumptionChart sensorId={sensorId} />
    </div>
  );
}
